import { useCentralApi, useCurrentUser } from "hooks/shared";
import { IMetadataOwner, IMetadataTab } from "models/metadata";
import { useEffect, useMemo, useState } from "react";


type UseWorkItemMetadataTabsResult = [
    IMetadataTab[],
    boolean
];

type UseWorkItemMetadataTabsSignature = (owner: IMetadataOwner, draft?: boolean) => UseWorkItemMetadataTabsResult;

const useWorkItemMetadataTabs: UseWorkItemMetadataTabsSignature = (owner: IMetadataOwner, draft?: boolean) => {
    
    const [ result, setResult ] = useState<IMetadataTab[]>([]);
    const [ loading, setLoading ] = useState<boolean>(false);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const { taskId, workItemId } = owner;
    const url = useMemo(() => {
        const query = taskId ? `?taskId=${taskId}` : ''
        return draft 
            ? `/api/v3/drafts/workitems/${workItemId}/metadata/tabs`
            : `/api/v3/workitems/${workItemId}/metadata/tabs${query}`
    }, [ draft, taskId, workItemId ]);

    useEffect(() => {
        const controller = new AbortController();

        if (!workItemId) {
            return;
        }

        (async () => {
            setLoading(true);
            try {
                const resp = await fetcher(url, {
                    signal: controller.signal
                });

                const data = await resp.json() as IMetadataTab[];
                setResult(data);
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            controller.abort();
            setLoading(false);
        }
    }, [ user, url ]);

    return [
        result,
        loading
    ];
};

export default useWorkItemMetadataTabs;